/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Unit detail header actions, by status:
 * - AVAILABLE: Hold (POST /re/units/{id}/holds), Book, Block
 * - HELD: Book (the booking converts the active hold)
 * - BLOCKED: Unblock (manual BLOCKED -> AVAILABLE)
 * Book opens the booking wizard with the unit preselected.
 */

import PropTypes from "prop-types";
import { useState } from "react";
import MDButton from "components/MDButton";
import { EntityFormDialog, apiErrorMessage } from "components/EntityFramework";
import { contactRef } from "./reEntityConfig";

// Built on first use: reEntityConfig imports this file, so contactRef is not
// initialised yet when this module is evaluated.
const holdFields = () => [
  { name: "hours", label: "Hold for (hours)", type: "number", required: true, defaultValue: 48 },
  { name: "eoi_amount", label: "EOI amount (AED)", type: "money" },
  contactRef("contact_id", "Client", "contact_name"),
  { name: "notes", label: "Notes", type: "textarea" },
];

const ReUnitActions = ({ unit, navigate, axiosInstance, refresh, notify, canEdit }) => {
  const [holdOpen, setHoldOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!unit?.id) return null;
  const { status } = unit;

  const setStatus = async (next) => {
    setBusy(true);
    try {
      await axiosInstance.post(`/re/units/${unit.id}/status`, { status: next });
      notify(next === "BLOCKED" ? "Unit blocked" : "Unit available");
      await refresh();
    } catch (error) {
      notify(apiErrorMessage(error, "Could not change the unit status"), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {status === "AVAILABLE" && canEdit ? (
        <MDButton variant="outlined" color="white" size="small" disabled={busy} onClick={() => setHoldOpen(true)}>
          Hold
        </MDButton>
      ) : null}
      {status === "AVAILABLE" || status === "HELD" ? (
        <MDButton
          variant="gradient"
          color="dark"
          size="small"
          onClick={() => navigate(`/real-estate/bookings/new?unit_id=${unit.id}`)}
        >
          Book
        </MDButton>
      ) : null}
      {status === "AVAILABLE" && canEdit ? (
        <MDButton variant="text" color="white" size="small" disabled={busy} onClick={() => setStatus("BLOCKED")}>
          Block
        </MDButton>
      ) : null}
      {status === "BLOCKED" && canEdit ? (
        <MDButton variant="outlined" color="white" size="small" disabled={busy} onClick={() => setStatus("AVAILABLE")}>
          Unblock
        </MDButton>
      ) : null}
      <EntityFormDialog
        open={holdOpen}
        onClose={() => setHoldOpen(false)}
        title={`Hold unit ${unit.unit_number || ""}`.trim()}
        fields={holdFields()}
        submitLabel="Hold unit"
        onSubmit={(payload) => axiosInstance.post(`/re/units/${unit.id}/holds`, payload)}
        onCreated={() => {
          notify("Unit held");
          refresh();
        }}
      />
    </>
  );
};

ReUnitActions.propTypes = {
  unit: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  axiosInstance: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  refresh: PropTypes.func.isRequired,
  notify: PropTypes.func.isRequired,
  canEdit: PropTypes.bool,
};

ReUnitActions.defaultProps = {
  canEdit: true,
};

export default ReUnitActions;
